'use client';
import clsx from 'clsx';
import React, { forwardRef, useRef, useMemo, useImperativeHandle, useEffect } from 'react';
import { useAutoplayInView } from './useAutoplayInView.mjs';

const ClientAutoplayVideo = forwardRef((props, ref) => {
  const {
    className,
    src,
    sources,
    poster,
    controls = false,
    loop = false,
    muted = true,
    playsInline = true,
    decorative = true,
    describedByText,
    captions,
    autoplayOptions,
    forceAttributes = true,
    style,
    ...rest
  } = props;
  const videoRef = useRef(null);
  const descId = useMemo(() => describedByText ? `vid-desc-${Math.random().toString(36).slice(2)}` : void 0, [describedByText]);
  useAutoplayInView(videoRef, autoplayOptions);
  useImperativeHandle(
    ref,
    () => ({
      play: () => videoRef.current?.play() ?? Promise.resolve(),
      pause: () => videoRef.current?.pause(),
      toggle: () => videoRef.current ? videoRef.current.paused ? videoRef.current.play() : (videoRef.current.pause(), Promise.resolve()) : Promise.resolve(),
      element: videoRef.current
    }),
    []
  );
  useEffect(() => {
    if (!forceAttributes) return;
    const el = videoRef.current;
    if (!el) return;
    const toggleAttr = (name, on) => {
      if (on) el.setAttribute(name, "");
      else el.removeAttribute(name);
    };
    toggleAttr("muted", muted);
    toggleAttr("controls", controls);
    toggleAttr("loop", loop);
    toggleAttr("playsinline", playsInline);
  }, [forceAttributes, muted, controls, loop, playsInline]);
  const a11yProps = decorative ? { "aria-hidden": true, tabIndex: -1 } : { "aria-describedby": descId };
  const hasSources = Array.isArray(sources) && sources.length > 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, describedByText && !decorative && /* @__PURE__ */ React.createElement("span", { id: descId, className: "sr-only" }, describedByText), /* @__PURE__ */ React.createElement(
    "video",
    {
      ref: videoRef,
      className: clsx("kohler-video-root", className),
      autoPlay: false,
      controls: decorative ? false : controls,
      loop,
      muted,
      playsInline,
      poster,
      src: hasSources ? void 0 : src,
      style,
      ...a11yProps,
      ...rest
    },
    hasSources && sources?.map((s) => /* @__PURE__ */ React.createElement("source", { key: s.src, ...s })),
    captions && !decorative && /* @__PURE__ */ React.createElement(
      "track",
      {
        kind: "captions",
        src: captions.src,
        srcLang: captions.srclang,
        label: captions.label,
        ...captions.default ? { default: true } : {}
      }
    )
  ));
});

export { ClientAutoplayVideo };
